import React, { useEffect, useCallback, useState, useRef, useMemo } from 'react';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { Tag, Space, notification, Modal, Button } from 'antd';
import { EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import { useUserLocale } from '../../hooks/useLocale';
import { useCaseList } from '../../hooks/cases/useCaseList';
import { APP_TERMS } from '../../app-terms';
import { AvailableAppLocales } from '../../availableAppLocales';
import ListPage from '../../components/core/ListPage';
import useListFilters from '../../hooks/useListFilters';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../../axios/apiService';
import { getCaseReviewStatusColor } from '../../utils/case';
import AssignRadiologist from './AssignRadiologist';
import {
  CASE_REVIEW_STATUS_ASSIGNED,
  CASE_REVIEW_STATUS_REJECTED,
  CASE_REVIEW_STATUS_SUMITTED,
  CASE_STATUS_COMPLETED,
  CASE_STATUS_SUBMITTED,
} from './CaseConstants';
const unassignRadiologistConfig = {
  title: 'Un-Assign Case Radiologist',
  content: 'Are you sure you want to un-assign the radiologist from the case?',
  okText: 'Yes',
  cancelText: 'No',
};
const acceptCaseConfig = {
  title: 'Accept Case From Radiologist',
  content: 'Are you sure you want to accept this case?',
  okText: 'Yes',
  cancelText: 'No',
};
const rejectCaseConfig = {
  title: 'Reject Case From Radiologist',
  content: 'Are you sure you want to reject this case?',
  okText: 'Yes',
  cancelText: 'No',
};
const deleteCaseConfig = {
  title: 'Cancel Case',
  content: 'Are you sure you want to cancel this case?',
  okText: 'Yes',
  cancelText: 'No',
};
const DELETE_NOT_ALLOWED_CASE_REVIEW_STATUS = [
  CASE_REVIEW_STATUS_ASSIGNED,
  CASE_REVIEW_STATUS_REJECTED,
];
const DELETE_NOT_ALLOWED_CASE_STATUS = [CASE_STATUS_COMPLETED];

const CASE_REVIEW_STATUS_URL_OPTIONS = [
  'accepted',
  'rejected',
  'submited',
  'assigned',
  'resolved',
];

const CaseList = ({
  title,
  caseStatus,
  breadCrumbItems,
  onItemDelete,
  hideFooter,
  showHasFilter,
  showProStatusFilter,
  forcedProStatus,
}) => {
  const [modal, modalContextHolder] = Modal.useModal();
  const [show, setShow] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState();
  const formRef = useRef(null);
  const isInitialized = useRef(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    search,
    setSearch,
    pagination,
    setPagination,
    sortParams,
    setSortParams,
    loading,
    setLoading,
    selectedRows,
    setSelectedRows,
  } = useListFilters();
  const urlSearchRaw = searchParams.get('search');
  const urlCaseReviewRaw = searchParams.get('caseReviewStatus');
  const normalizedUrlReview =
    urlCaseReviewRaw &&
    CASE_REVIEW_STATUS_URL_OPTIONS.includes(urlCaseReviewRaw.toLowerCase())
      ? urlCaseReviewRaw.toLowerCase()
      : null;
  const isLegacySubmittedSearch =
    showProStatusFilter &&
    (urlSearchRaw === CASE_STATUS_SUBMITTED ||
      urlSearchRaw === CASE_REVIEW_STATUS_SUMITTED);
  const initialProStatusFilter = forcedProStatus
    ? forcedProStatus
    : isLegacySubmittedSearch
      ? 'submited'
      : showProStatusFilter && normalizedUrlReview
        ? normalizedUrlReview
        : 'all';

  const { userLocale } = useUserLocale();
  const { data, getCaseList } = useCaseList();
  const [api, contextHolder] = notification.useNotification();
  const [hasRushstatusFilter, setHasRushStatusFilterState] = useState(
    () => searchParams.get('rushOnly') === 'true'
  );
  const [proStatusFilter, setProStatusFilter] = useState(initialProStatusFilter);
  const proStatusForRequest = forcedProStatus ?? proStatusFilter;

  const fetchCases = useCallback(
    async () => {
      setLoading(true);
      try {
        const response = await getCaseList({
          proStatus: proStatusForRequest,
          hasRush: hasRushstatusFilter,
          search: search,
          page: pagination.current,
          limit: pagination.pageSize,
          sortOrder: sortParams.sortOrder,
          sortBy: sortParams.sortField,
          caseStatus,
        });
        setLoading(false);
        setPagination({ total: response.totalItems });
      } catch (error) {
        setLoading(false);
      }
    },
    [
      pagination.current,
      pagination.pageSize,
      sortParams.sortOrder,
      sortParams.sortField,
      search,
      caseStatus,
      hasRushstatusFilter,
      proStatusForRequest,
      setLoading,
      setPagination,
    ]
  );

  const reload = useCallback(() => fetchCases(), [fetchCases]);

  const handleRefresh = useCallback(() => reload(), [
    pagination.current,
    pagination.pageSize,
    sortParams.sortOrder,
    sortParams.sortField,
    search,
    caseStatus,
    hasRushstatusFilter,
    proStatusForRequest
  ]);

  // modal
  const showModal = () => setShow(true);
  const hideModal = () => {
    formRef?.current?.reset?.();
    setShow(false);
    setSelectedRecord();
  };
  const handleCancel = () => {
    hideModal();
  };
  const handleSuccess = () => {
    setSelectedRows([]);
    hideModal();
    reload();
  };
  const handleError = () => { };
  const hashRushOnChange = (checked) => {
    setHasRushStatusFilterState(!!checked);
    const params = new URLSearchParams(searchParams);
    if (checked) {
      params.set('rushOnly', 'true');
    } else {
      params.delete('rushOnly');
    }
    params.set('page', '1');
    setSearchParams(params);
  };
  const proStatusOnChange = (value) => {
    if (forcedProStatus) return;
    const next = value ?? 'all';
    setProStatusFilter(next);
    const params = new URLSearchParams(searchParams);
    if (!showProStatusFilter) return;
    if (next === 'all') {
      params.delete('caseReviewStatus');
    } else {
      params.set('caseReviewStatus', next);
    }
    params.set('page', '1');
    setSearchParams(params);
  };
  const handleTableChange = (newPagination, _, sorter) => {
    const newSortOrder = Array.isArray(sorter) ? undefined : sorter.order;
    const newSortField = Array.isArray(sorter) ? undefined : sorter.field;
    setSortParams({ sortOrder: newSortOrder, sortField: newSortField });

    const pageSizeChanged = newPagination?.pageSize !== pagination.pageSize;
    const resolvedPage = pageSizeChanged ? 1 : (newPagination?.current || 1);

    const params = new URLSearchParams(searchParams);
    if (newSortField && newSortOrder) {
      params.set('sort', `${newSortField}:${newSortOrder}`);
    } else {
      params.delete('sort');
    }
    params.set('page', String(resolvedPage));
    params.set('limit', String(newPagination?.pageSize || 10));
    setSearchParams(params);
  };

  const handleSearch = (searchValue) => {
    setSearch(searchValue);
  };

  const handleAdd = () => navigate('/user/case/add');
  const handleAssign = (records) => {
    setSelectedRecord(records);
    showModal();
  };
  const handleAccept = async (record) => {
    const confirmed = await modal.confirm(acceptCaseConfig);

    if (!confirmed) return;
    try {
      setLoading(true);
      await apiService.acceptCaseFromRadioLogist(record?.caseId);
      api.success({
        message: 'Success',
        description: 'Case accepted successfully',
      });
      reload();
    } catch (error) {
      api.error({
        description: error?.response?.data?.error || 'Error',
        message:
          error?.response?.data?.message ||
          'Error occured while accepting the case',
      });
    } finally {
      setLoading(false);
    }
  };
  const handleReject = async (record) => {
    const confirmed = await modal.confirm(rejectCaseConfig);

    if (!confirmed) return;
    try {
      setLoading(true);
      await apiService.rejectCaseFromRadioLogist(record?.caseId);
      api.success({
        message: 'Success',
        description: 'Case rejected successfully',
      });
      reload();
    } catch (error) {
      api.error({
        description: error?.response?.data?.error || 'Error',
        message:
          error?.response?.data?.message ||
          'Error occured while rejecting the case',
      });
    } finally {
      setLoading(false);
    }
  };
  const handleUnAssign = async (record) => {
    const confirmed = await modal.confirm(unassignRadiologistConfig);

    if (!confirmed) return;
    try {
      setLoading(true);
      await apiService.unAssignCaseToRadioLogist(record?.caseId);
      api.success({
        message: 'Success',
        description: 'Case un-assigned successfully',
      });
      reload();
    } catch (error) {
      api.error({
        description: error?.response?.data?.error || 'Error',
        message:
          error?.response?.data?.message ||
          'Error occured while un-assigning the case',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) =>
    navigate(`/user/case/edit?id=${record?.caseId}`);
  const handleView = (record) =>
    navigate(`/user/case/view?id=${record?.caseId}`);

  const handleDelete = async (record) => {
    const confirmed = await modal.confirm(deleteCaseConfig);

    if (!confirmed) return;
    try {
      setLoading(true);
      const response = await apiService.deleteCase(record?.caseId);
      api.success({
        message: 'Success',
        description: response?.message,
        showProgress: false,
      });
      reload();
      onItemDelete?.();
    } catch (error) {
      api.error({
        message: 'Error',
        description: error?.message,
        showProgress: false,
      });
    } finally {
      setLoading(false);
    }
  };
  const canDeleteCase = (record) => {
    if (DELETE_NOT_ALLOWED_CASE_STATUS.includes(record?.caseStatus))
      return false;
    if (
      DELETE_NOT_ALLOWED_CASE_REVIEW_STATUS.includes(record?.caseReviewStatus)
    )
      return false;
    return true;
  };
  const handleBulkAssign = handleAssign;
  const handleBulkDelete = async (records) => {
    const confirmed = await modal.confirm(deleteCaseConfig);
    if (!confirmed) return;
    if (!records?.length) return;
    const caseIds = records
      ?.filter(canDeleteCase)
      ?.map((record) => record?.caseId);
    try {
      setLoading(true);
      const response = await apiService.bulkDeleteCase(caseIds);
      api.success({
        message: 'Success',
        description: response?.message,
        showProgress: false,
      });
      reload();
    } catch (error) {
      api.error({
        message: 'Error',
        description: error?.message,
        showProgress: false,
      });
    } finally {
      setLoading(false);
    }
  };
  const onActionItemClick = ({ key }, record) => {
    switch (key) {
      case 'Assign':
        handleAssign([record]);
        break;
      case 'UnAssign':
        handleUnAssign(record);
        break;
      case 'Accept':
        handleAccept(record);
        break;
      case 'Reject':
        handleReject(record);
        break;
      case 'View':
        handleView(record);
        break;
      case 'Edit':
        handleEdit(record);
        break;
      case 'Delete':
        handleDelete(record);
        break;

      default:
        break;
    }
  };

  // Sync CaseList-specific URL params (sort, rush, pro status, legacy redirect)
  useEffect(() => {
    const sortParam = searchParams.get('sort');
    const searchParam = searchParams.get('search');
    const rushOnlyParam = searchParams.get('rushOnly');
    const caseReviewParam = searchParams.get('caseReviewStatus');
    const legacySubmitted =
      showProStatusFilter &&
      (searchParam === CASE_STATUS_SUBMITTED ||
        searchParam === CASE_REVIEW_STATUS_SUMITTED);

    if (sortParam) {
      const [field, order] = sortParam.split(':');
      if (field && order) {
        setSortParams({ sortField: field, sortOrder: order });
      }
    }

    if (legacySubmitted) {
      const p = new URLSearchParams(searchParams);
      p.delete('search');
      p.set('caseReviewStatus', 'submited');
      p.set('page', '1');
      setSearchParams(p, { replace: true });
    }

    setHasRushStatusFilterState(rushOnlyParam === 'true');

    const normalizedReview =
      caseReviewParam &&
      CASE_REVIEW_STATUS_URL_OPTIONS.includes(caseReviewParam.toLowerCase())
        ? caseReviewParam.toLowerCase()
        : null;
    const nextProStatus = forcedProStatus
      ? forcedProStatus
      : showProStatusFilter && normalizedReview
        ? normalizedReview
        : 'all';
    setProStatusFilter(nextProStatus);

    isInitialized.current = true;
  }, [searchParams, forcedProStatus, showProStatusFilter]);

  useEffect(() => {
    if (isInitialized.current) {
      reload();
    }
  }, [
    pagination.current,
    pagination.pageSize,
    sortParams.sortOrder,
    sortParams.sortField,
    search,
    caseStatus,
    hasRushstatusFilter,
    proStatusForRequest
  ]);
  const actionItems = [
    { key: 'View', label: 'View' },
  ];
  const columns = [
    {
      title: 'Case ID',
      dataIndex: 'caseId',
    },
    {
      title: 'P. Name',
      dataIndex: 'patientName',
    },
    {
      title: 'Referring Doctor',
      dataIndex: 'doctorName',
      sorter: true,
    },
    {
      title: 'Assigned To (Radiologist)',
      dataIndex: 'radioLogistUserSub',
      render: (value, record) => {
        return value ? (
          <div>
            <span>{record?.radioLogistName}</span>
            <br />
            {/* <span className="text-muted">{record?.radioLogistEmail}</span> */}
          </div>
        ) : (
          <div>N/A</div>
        );
      },
    },
    {
      title: 'Rush Status',
      key: 'hasRush',
      dataIndex: 'hasRush',
      render: (value, record, index) => {
        // const statusColor = getCaseStatusColor(record?.caseStatus);
        return (
          value ?
            <Tag color={"red"} key={index}>
              RUSH
            </Tag> : "-"
        );
      },
      sorter: true,
    },
    // {
    //   title: 'Status',
    //   key: 'status',
    //   dataIndex: 'caseStatus',
    //   render: (value, record, index) => {
    //     const statusColor = getCaseStatusColor(record?.caseStatus);
    //     return (
    //       <Tag color={statusColor} key={index}>
    //         {value}
    //       </Tag>
    //     );
    //   },
    //   sorter: true,
    // },
    {
      title: 'Processing Status',
      key: 'reviewStatus',
      dataIndex: 'caseReviewStatus',
      render: (value, record, index) => {
        const statusColor = getCaseReviewStatusColor(record?.caseReviewStatus);
        return (
          <Tag color={statusColor} key={index}>
            {value}
          </Tag>
        );
      },
      sorter: true,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      render: (text) =>
        format(new Date(text), APP_TERMS.date_1_format, {
          locale: AvailableAppLocales?.[userLocale],
        }),
      sorter: true,
    },
    {
      title: '',
      dataIndex: '',
      render: (text, record) => {
        const isDeleteAllowed = (caseReviewStatus, caseStatus, key) =>
          !(
            (DELETE_NOT_ALLOWED_CASE_REVIEW_STATUS.includes(caseReviewStatus) ||
              DELETE_NOT_ALLOWED_CASE_STATUS.includes(caseStatus)) &&
            key === 'Delete'
          );
        let items = [
          ...(actionItems?.filter((action) => {
            return isDeleteAllowed(
              record?.caseReviewStatus,
              record?.caseStatus,
              action?.key
            );
          }) || []),
        ];
        switch (record?.caseReviewStatus) {
          case CASE_REVIEW_STATUS_ASSIGNED:
          case CASE_REVIEW_STATUS_REJECTED:
            items = [...items, { key: 'UnAssign', label: 'Unassign' }];
            break;

          default:
            if (!record?.caseReviewStatus && record?.caseStatus === CASE_STATUS_SUBMITTED) {
              items = [...items, { key: 'Assign', label: 'Assign' }];
            }
            break;
        }

        if (record?.caseStatus === CASE_REVIEW_STATUS_SUMITTED && record?.caseReviewStatus !== CASE_REVIEW_STATUS_ASSIGNED) {
          items = [...items, { key: 'Edit', label: 'Process' }, { key: 'Delete', label: 'Delete' },];
        }
        return (
          <>
            <Space size="middle">
              {items?.map((item) => (
                <Button
                  key={item?.key}
                  type="primary"
                  danger={item?.key === 'Delete'}
                  onClick={() => onActionItemClick(item, record)}
                >
                  {
                    item?.key === 'View' ? (
                      <EyeOutlined />
                    ) : item?.key === 'Delete' ? (
                      <DeleteOutlined />
                    ) : (
                      item?.label
                    )
                  }
                </Button>
              ))}
            </Space>
            {modalContextHolder}
          </>
        );
      },
    },
  ];

  const selectedRowKeys = useMemo(() => selectedRows?.map((record) => record?.caseId), [selectedRows]);

  const proStatusSelectValue =
    proStatusForRequest === 'all' ? undefined : proStatusForRequest;

  return (
    <>
      {contextHolder}
      <ListPage
        title={title}
        data={data?.items || []}
        columns={columns}
        loading={loading}
        pagination={pagination}
        rowKey={(record) => record?.caseId}
        onAdd={handleAdd}
        onRefresh={handleRefresh}
        onSearch={handleSearch}
        onTableChange={handleTableChange}
        breadCrumbItems={breadCrumbItems}
        hideAddAction={true}
        onSelectRowsfun={setSelectedRows}
        selectedRowKeys={selectedRowKeys}
        footer={
          !hideFooter &&
          selectedRows &&
          selectedRows.length > 0 && (
            <>
              <Button
                type="primary"
                className="m-2"
                onClick={() => handleBulkAssign(selectedRows)}
              >
                Assign Selected
              </Button>
              <Button
                type="primary"
                danger
                loading={loading}
                onClick={() => handleBulkDelete(selectedRows)}
              >
                Delete Selected
              </Button>
            </>
          )
        }
        hasRushstatusFilter={hasRushstatusFilter}
        setHasRushStatusFilter={hashRushOnChange}
        showHasFilter={showHasFilter}
        showProStatusFilter={showProStatusFilter}
        proStatusOnChange={proStatusOnChange}
        proStatusFilterValue={proStatusSelectValue}
        initialSearchString={search}
        
      />
      <Modal
        title={'Assign Radiologist'}
        open={show}
        onCancel={handleCancel}
        maskClosable={false}
        footer={null}
      >
        <AssignRadiologist
          records={selectedRecord}
          ref={formRef}
          onSuccess={handleSuccess}
          onError={handleError}
        />
      </Modal>
    </>
  );
};

CaseList.propTypes = {
  title: PropTypes.string.isRequired,
  caseStatus: PropTypes.string.isRequired,
  breadCrumbItems: PropTypes.array,
  onItemDelete: PropTypes.func,
  hideFooter: PropTypes.bool,
  showHasFilter: PropTypes.bool,
  showProStatusFilter: PropTypes.bool,
  proStatusOnChange: PropTypes.func,
  forcedProStatus: PropTypes.string,
};

export default CaseList;
